import { CHARTS_V2, CommonChartProps } from 'features/visualizations';
import { useIntl } from 'react-intl';

import { StratRowType } from './startifications.config';

import type { StratCardContentProps } from './StratCard';

export function StratChart(props: StratCardContentProps) {
  const intl = useIntl();
  if (props.data.length === 0) return <div>NO DATA</div>;
  if (!props.config) return <div>NO CONFIG</div>;

  const formatter = (value: string) => value;

  const valueField =
    'valueField' in props.config && props.config.valueField
      ? props.config.valueField
      : 't_current_balance_cents';

  const valueFormatOptions =
    'valueFormatOptions' in props.config && props.config.valueFormatOptions
      ? props.config.valueFormatOptions
      : {
          style: 'currency' as const,
          currency: 'USD',
          maximumFractionDigits: 0,
          prepareNumber: (num: number) => num / 100,
        };

  const yAxisLabel =
    'yAxisLabel' in props.config && props.config.yAxisLabel ? props.config.yAxisLabel : 'UPB';

  const chartProps: CommonChartProps<StratRowType> = {
    data: props.data,
    mode: props?.mode ?? 'card',
    keyField: 'key',
    valueField: valueField,
    formatKey: formatter,
    height: props.height,
    width: props.width,
    valueFormatOptions: valueFormatOptions,
  };

  const avgField = 'avgField' in props.config ? props.config.avgField : null;
  const avgFormat = 'avgFormat' in props.config ? props.config.avgFormat : null;
  const avgValue = avgField ? (props.avgSummary[avgField] as number | undefined) : null;
  const avgFormatted =
    avgValue && avgFormat ? avgFormat(intl, avgValue) : avgValue?.toString() || '-';
  const centralLabel = 'avgLabel' in props.config ? props.config.avgLabel : 'AVERAGE';

  // Use enhanced v2 charts
  switch (props.config.chart) {
    case 'Bar_Vertical_Dots':
      return (
        <CHARTS_V2.Bar_Vertical_Dots<StratRowType>
          {...chartProps}
          yAxisLabel={yAxisLabel}
          height={props.height}
          width={props.width}
        />
      );
    case 'Bar_Horizontal_Dots':
      return (
        <CHARTS_V2.Bar_Horizontal_Dots<StratRowType>
          {...chartProps}
          yAxisLabel={yAxisLabel}
          height={props.height}
          width={props.width}
        />
      );
    case 'Bar_Vertical':
      return (
        <CHARTS_V2.Bar_Vertical<StratRowType>
          {...chartProps}
          yAxisLabel={yAxisLabel}
          height={props.height}
          width={props.width}
        />
      );
    case 'Bar_Horizontal':
      return (
        <CHARTS_V2.Bar_Horizontal<StratRowType>
          {...chartProps}
          yAxisLabel={yAxisLabel}
          height={props.height}
          width={props.width}
        />
      );
    case 'Pie':
      return (
        <CHARTS_V2.Pie
          {...chartProps}
          centralValue={avgFormatted}
          centralLabel={centralLabel}
          height={props.height}
          width={props.width}
        />
      );
    case 'States':
      // States chart still uses v1 implementation
      return <CHARTS_V2.States {...chartProps} />;
    default:
      return null;
  }
}
